<template>
  <q-card class="q-pa-md">
    <div class="row">
      <q-space />
      <div class="row">
        <q-select
          dense
          outlined
          class="q-mr-sm"
          v-model="transactionModel"
          :options="transactionList"
          label="Transaction Type"
          style="width: 210px; padding-bottom: 10px"
        />

        <q-select
          outlined
          v-model="selectedSector"
          :options="optionSectors"
          dense
          label="Choose sector"
          single
          input-debounce="0"
          option-label="name"
          option-value="symbol"
          class="q-mr-sm"
          style="width: 210px; padding-bottom: 10px"
          :menu-absolute="true"
        />

        <q-select
          :menu-absolute="true"
          outlined
          class="q-mb-md"
          :class="$q.dark.mode ? '' : 'bg-grey-1'"
          v-model.lazy="selectedBroker"
          single
          dense
          clearable
          use-input
          input-debounce="0"
          :options="options"
          option-value="broker_no"
          option-label="broker_name"
          emit-value
          stack-label
          map-options
          label="Select broker"
          filter
          @filter="filterFn"
        >
          <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section avatar>
                <q-item-label class="text-bold">{{
                  scope.opt.broker_no
                }}</q-item-label>
              </q-item-section>
              <q-item-section>
                <q-item-label>
                  <div class="row">
                    {{ scope.opt.broker_name }}
                  </div>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:no-option>
            <q-item>
              <q-item-section class="text-grey"> No results </q-item-section>
            </q-item>
          </template>
        </q-select>

        <div class="q-px-xs col-sm-">
          <q-input
            dense
            filled
            v-model="from_date"
            mask="date"
            :rules="['date']"
            hint="Enter From Date"
          >
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy
                  cover
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date v-model="from_date">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Close" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>
        <div class="q-px-xs col-sm-">
          <q-input
            dense
            filled
            v-model="to_date"
            mask="date"
            :rules="['date']"
            hint="Enter To Date"
          >
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy
                  cover
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date v-model="to_date">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Close" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>
        <div class="q-pa-xs text-end col-sm-">
          <q-btn
            class="q-pa-sm"
            square
            color="blue-5"
            icon="search"
            @click="loadData()"
            size="sm"
          >
            <q-tooltip class="bg-primary">Search</q-tooltip>
          </q-btn>
        </div>
      </div>
    </div>
    <q-table
      :class="
        $q.dark.mode
          ? 'table-sticky-header-column-dark '
          : displayAll
          ? 'table-sticky-header-column table-column-color-all'
          : 'table-sticky-header-column table-column-color'
      "
      dense
      :rows="brokerAnalyticsDataComputed"
      :columns="
        displayAll
          ? transactionModel === 'Purchase'
            ? purchaseColumns
            : sellColumns
          : transactionModel === 'Purchase'
          ? purchaseColumnsBroker
          : sellColumnsBroker
      "
      binary-state-sort
      :pagination="dynamicPagination"
      separator="cell"
      :filter="filterQuery"
      :filter-method="filterBrokerAnalytics"
    >
      <template v-slot:top-left>
        <div class="row">
          <span>
            <q-input
              class="q-mr-md live-search"
              bordered
              dense
              debounce="300"
              v-model="filterSymbol"
              placeholder="Filter Symbols"
            >
            </q-input>
          </span>
          <span>
            <q-input
              v-if="displayAll"
              bordered
              dense
              debounce="300"
              v-model="filterBuyer"
              placeholder="Filter Broker"
            >
            </q-input>
          </span>
          <span
            v-if="!displayAll"
            class="q-py-sm q-pl-sm-md text-weight-medium"
          >
            Broker No. {{ selectedBroker }}
          </span>
        </div>
      </template>
      <template v-slot:top-right>
        <span>
          <q-checkbox
            class="q-mr-sm"
            v-model="myStocks"
            color="blue-5"
            keep-color
            label="My Signals"
          >
            <q-tooltip class="bg-primary">My Signals Only</q-tooltip>
          </q-checkbox>
        </span>
      </template>
      <template v-slot:body-cell-symbol="props">
        <q-td :props="props">
          <q-item
            dense
            clickable
            :to="{
              path: '/stock-profile',
              query: { symbol: props.value },
            }"
          >
            <div class="col">
              {{ props.value }}
              <q-tooltip>{{
                allStocks.find((stock) => stock.symbol == props.value)?.name
              }}</q-tooltip>
            </div>
          </q-item>
        </q-td>
      </template>
    </q-table>
  </q-card>
</template>
<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import useUtils from "src/use/utils";
import useData from "src/use/data";

const { brokerAnalyticsWithBroker, brokerAnalytics, brokerList } = useData();
const { amountConversion, utilsOptionSectors } = useUtils();
const store = useStore();
const $route = useRoute();
const $q = useQuasar();

const selectedBroker = ref(null);
const displayAll = ref(true);

let myStocks = ref(false);
const myStocksList = ref([]);
const sectorSymbols = ref([]);
const transactionModel = ref(null);
const transactionList = ref(["Purchase", "Selling"]);

const optionSectors = ref(utilsOptionSectors());

const selectedSector = ref(optionSectors.value[0]);
const allStocks = ref(store.state.core.allStocks);

selectedBroker.value = $route.query?.brokerID || null;
transactionModel.value = $route.query?.transaction || "Purchase";

const brokerLists = ref([]);
const options = ref([]);

const buyBrokerAnalyticsData = ref([]);
const sellBrokerAnalyticsData = ref([]);

const to_date = ref(store.state.market.asOf.replaceAll("/", "-"));
const parsedDate = new Date(to_date.value);
const from_date = ref(null);
const from_dateCalculated = ref(
  new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate() - 7
  )
    .toISOString()
    .substring(0, 10)
);

brokerList().then((res) => {
  brokerLists.value = res.data;
  // selectedBroker.value = res.data[0].broker_no;
});

const columns = [
  {
    name: "symbol",
    required: true,
    label: "Symbol",
    field: "symbol",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "left",
  },
  {
    name: "buyer",
    label: "Buyer",
    field: "buyer",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "buy_quantity",
    label: "Buy Qnty",
    field: (row) => row.buy_quantity,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  {
    name: "avg_buy_amount",
    label: "Avg Buy Amt",
    field: "avg_buy_amount",
    field: (row) => row.avg_buy_amount,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  {
    name: "avg_buy_rate",
    label: "Avg Buy Rate",
    field: "net_buy_rate",
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
  {
    name: "seller",
    label: "Seller",
    field: "seller",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "sell_quantity",
    label: "Sell Qnty",
    field: (row) => row.sell_quantity,
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
  {
    name: "avg_sell_amount",
    label: "Avg Sell Amt",
    field: (row) => row.avg_sell_amount,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  {
    name: "avg_sell_rate",
    label: "Avg Sell Rate",
    field: "net_sell_rate",
    sortable: true,
    format: (val) => amountConversion(val),
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "net_amount",
    label: "Net Amt",
    field: "net_amount",
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },

  {
    name: "net_rate",
    label: "Net Rate",
    field: "net_rate",
    sortable: true,
    format: (val) => amountConversion(val),

    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
];

const purchaseColumns = [
  ...columns.slice(0, 9),
  {
    name: "net_quantity",
    label: "Net Holding",
    field: (row) => row.net_quantity,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  ...columns.slice(9),
];

const sellColumns = [
  ...columns.slice(0, 9),
  {
    name: "net_quantity",
    label: "Net Supply",
    field: (row) => row.net_quantity,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  ...columns.slice(9),
];

const columnsBroker = [
  {
    name: "symbol",
    required: true,
    label: "Symbol",
    field: "symbol",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "left",
  },
  {
    name: "buy_quantity",
    label: "Buy Qnty",
    field: (row) => row.buy_quantity,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  {
    name: "avg_buy_amount",
    label: "Avg Buy Amt",
    field: "avg_buy_amount",
    field: (row) => row.avg_buy_amount,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  {
    name: "net_buy_rate",
    label: "Net Buy Rate",
    field: "net_buy_rate",
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
  {
    name: "sell_quantity",
    label: "Sell Qnty",
    field: (row) => row.sell_quantity,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  {
    name: "avg_sell_amount",
    label: "Avg Sell Amt",
    field: (row) => row.avg_sell_amount,
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
  {
    name: "net_sell_rate",
    label: "Net Sell Rate",
    field: "net_sell_rate",
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
  {
    name: "net_amount",
    label: "Net Amt",
    field: "net_amount",
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },

  {
    name: "net_rate",
    label: "Net Rate",
    field: "net_rate",
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
];

const purchaseColumnsBroker = [
  ...columnsBroker.slice(0, 7),
  {
    name: "net_quantity",
    label: "Net Holding",
    field: (row) => row.net_quantity,
    sortable: true,
    format: (val) => amountConversion(val),
    align: "center",
  },
  ...columnsBroker.slice(7),
];
const sellColumnsBroker = [
  ...columnsBroker.slice(0, 7),
  {
    name: "net_quantity",
    label: "Net Supply",
    field: (row) => row.net_quantity,
    sortable: true,
    format: (val) => amountConversion(val),

    align: "center",
  },
  ...columnsBroker.slice(7),
];

const filterSymbol = ref("");
const filterBuyer = ref("");

const filterQuery = computed({
  get() {
    return {
      symbol: filterSymbol.value?.toUpperCase(),
      buyer: filterBuyer.value?.toLowerCase(),
    };
  },
  set(newValue) {
    filterSymbol.value = newValue.symbol;
    filterBuyer.value = newValue.buyer;
  },
});

const filterBrokerAnalytics = (rows, terms, cols, getCellValue) => {
  return rows.filter((row) => {
    return (
      (filterQuery.value.symbol === "" ||
        row.symbol.toUpperCase().includes(filterQuery.value.symbol)) &&
      (filterQuery.value.buyer === "" ||
        row.buyer?.toLowerCase() === filterQuery.value.buyer)
    );
  });
};

function loadData() {
  $q.loading.show({});
  if (selectedBroker.value === null) {
    brokerAnalytics(
      from_date.value.replaceAll("/", "-"),
      to_date.value.replaceAll("/", "-")
    )
      .then((res) => {
        buyBrokerAnalyticsData.value = res.data.filter((i) => i.net_amount > 0);
        sellBrokerAnalyticsData.value = res.data.filter(
          (i) => i.net_amount < 0
        );

        $q.loading.hide();
        displayAll.value = true;
      })
      .catch((error) => {
        $q.loading.hide();
      });
  } else {
    brokerAnalyticsWithBroker(
      from_date.value.replaceAll("/", "-"),
      to_date.value.replaceAll("/", "-"),
      selectedBroker.value
    )
      .then((res) => {
        $q.loading.hide();
        buyBrokerAnalyticsData.value = res.data.filter((i) => i.net_amount > 0);
        sellBrokerAnalyticsData.value = res.data.filter(
          (i) => i.net_amount < 0
        );
        displayAll.value = false;
      })
      .catch((error) => {
        $q.loading.hide();
      });
  }
}

function filterData(sector, data, myStocksList) {
  if (sector.id === 0) {
    return !myStocks.value
      ? data
      : data.filter((i) => myStocksList.includes(i.symbol));
  } else {
    sectorSymbols.value = allStocks.value
      .filter((i) => i.sector == sector.id)
      .map((i) => i.symbol);

    return !myStocks.value
      ? data.filter((i) => sectorSymbols.value.includes(i.symbol))
      : data
          .filter((i) => sectorSymbols.value.includes(i.symbol))
          .filter((i) => myStocksList.includes(i.symbol));
  }
}

function filterFn(val, update) {
  if (val === "") {
    update(() => {
      options.value = brokerLists.value;
    });
    return;
  }

  update(() => {
    const needle = val;
    options.value = brokerLists.value.filter((v) => {
      return (
        v.broker_name.toLowerCase().indexOf(needle) > -1 ||
        v.broker_no.toString().toLowerCase().indexOf(needle) > -1
      );
    });
  });
}

const brokerAnalyticsDataComputed = computed(() => {
  const buyData = buyBrokerAnalyticsData.value || [];
  const sellData = sellBrokerAnalyticsData.value || [];

  const brokerData =
    transactionModel.value === "Purchase"
      ? [...buyData].sort((a, b) => b.net_amount - a.net_amount)
      : [...sellData].sort((a, b) => a.net_amount - b.net_amount);

  return filterData(selectedSector.value, brokerData, myStocksList.value);
});

watch(
  () => $route.query,
  (newQuery) => {
    if ("brokerID" in newQuery) {
      from_date.value = to_date.value;
    } else {
      from_date.value = from_dateCalculated.value;
    }
  },
  { immediate: true }
);

const paginationBuy = ref({
  rowsPerPage: 20,
  // sortBy: "net_amount",
  descending: true,
});

const paginationSell = ref({
  rowsPerPage: 20,
  // sortBy: "net_amount",
  descending: false,
});

const dynamicPagination = computed(() =>
  transactionModel.value === "Purchase"
    ? paginationBuy.value
    : paginationSell.value
);

onMounted(async () => {
  loadData();
});
</script>
